
import React, { createContext, useContext, useState } from 'react';
import { BookingDetails, PlantRange, ServiceFrequency, ServiceType, ServiceZone, AddOn } from '@/types';

interface BookingContextType {
  bookingDetails: BookingDetails | null;
  setBookingDetails: (details: BookingDetails | null) => void;
  clearBooking: () => void;
  addOns: AddOn[];
  setAddOns: (addOns: AddOn[]) => void;
  updateAddOn: (id: string, selected: boolean) => void;
  calculateTotalPrice: () => number;
  selectedAddOns: AddOn[];
}

const BookingContext = createContext<BookingContextType | undefined>(undefined);

// Default add-ons with updated disposal fee
const defaultAddOns: AddOn[] = [
  {
    id: "cleanup",
    name: "One-Time Cleanup",
    description: "Deep cleaning of your plant area (indoor or outdoor, e.g., removing debris, tidying pots).",
    price: 350,
    selected: false
  },
  {
    id: "replacement",
    name: "Plant Replacement",
    description: "Replace a plant; cost varies by plant type (e.g., $25 for a small cactus, $225 for a large shrub).",
    price: 150, // Average price
    selected: false
  },
  {
    id: "pestcontrol",
    name: "Pest Control or Soil Treatment",
    description: "Treat pests or improve soil for heat resistance (e.g., pest control for spider mites common in summer heat).",
    price: 90,
    selected: false
  },
  {
    id: "pruning",
    name: "Pruning (Trees, Cactus, Agave)",
    description: "Prune trees, cactus, or agave plants to keep them healthy and shaped (up to 6 plants per service).",
    price: 175,
    selected: false
  },
  {
    id: "hauling",
    name: "Hauling - Debris Removal",
    description: "Removal and disposal of garden debris, dead plants, or other yard waste from your property.",
    price: 300, // Updated from $175 to $300
    selected: false
  }
];

export function BookingProvider({ children }: { children: React.ReactNode }) {
  const [bookingDetails, setBookingDetails] = useState<BookingDetails | null>(null);
  const [addOns, setAddOns] = useState<AddOn[]>(defaultAddOns);

  const clearBooking = () => {
    setBookingDetails(null);
    // Reset add-ons to defaults (unselected)
    setAddOns(defaultAddOns.map(addon => ({...addon, selected: false})));
  };

  const updateAddOn = (id: string, selected: boolean) => {
    const updatedAddOns = addOns.map(addOn => 
      addOn.id === id ? { ...addOn, selected } : addOn
    );
    setAddOns(updatedAddOns);
  };

  const calculateTotalPrice = (): number => {
    if (!bookingDetails) return 0;

    let total = bookingDetails.price;

    // Add selected add-ons
    addOns.forEach(addOn => {
      if (addOn.selected) {
        total += addOn.price;
      }
    });

    return total;
  };
  
  // Get only selected add-ons
  const selectedAddOns = addOns.filter(addOn => addOn.selected);

  return (
    <BookingContext.Provider value={{ 
      bookingDetails, 
      setBookingDetails, 
      clearBooking, 
      addOns, 
      setAddOns, 
      updateAddOn,
      calculateTotalPrice,
      selectedAddOns
    }}>
      {children}
    </BookingContext.Provider>
  );
}

export function useBooking() {
  const context = useContext(BookingContext);
  if (context === undefined) {
    throw new Error('useBooking must be used within a BookingProvider');
  }
  return context;
}
